import ease from 'easy-ease';

const SCROLL_THRESHOLD = 20;

/**
 * Returns current Y axis scroll position.
 *
 * @returns {number}
 */
function getScrollY() {
	const pageOffsetSupported = window.scrollY !== undefined;
	const isCSS1Compatible = (document.compatMode || '') === 'CSS1Compat';

	return pageOffsetSupported
		? window.scrollY
		: isCSS1Compatible
		? document.documentElement.scrollTop
		: document.body.scrollTop;
}

/**
 * Returns position to which we will be scrolling
 *
 * @param {(string | Object)} element Selector or DOM element
 * @param {number} additionalOffset Add an additional offset to the final position. if > 0 then page is moved to
 *        the bottom otherwise the page is moved to the top.
 * @param {('top' | 'middle' | 'bottom')} [alignment='top'] Alignment of the element in the resulting viewport.
 *        Can be one of 'top', 'middle' or 'bottom'. Defaulting to 'top'
 *
 * @returns {number}
 */
function calculateScrollOffset(element, additionalOffset, alignment) {
	if (typeof element === 'string') {
		element = document.querySelector(element);
	}

	if (!element) {
		return 0;
	}

	const body = document.body;
	const html = document.documentElement;

	const elemRect = element.getBoundingClientRect();
	const clientHeight = html.clientHeight;
	const documentHeight = Math.max(
		body.scrollHeight,
		body.offsetHeight,
		clientHeight,
		html.scrollHeight,
		html.offsetHeight
	);

	additionalOffset = additionalOffset || 0;

	let scrollPosition;
	if (alignment === 'bottom') {
		scrollPosition = elemRect.bottom - clientHeight;
	} else if (alignment === 'middle') {
		scrollPosition = elemRect.bottom - clientHeight / 2 - elemRect.height / 2;
	} else {
		scrollPosition = elemRect.top;
	}

	const maxScrollPosition = documentHeight - clientHeight;
	return Math.min(
		scrollPosition + additionalOffset + window.pageYOffset,
		maxScrollPosition
	);
}

/**
 * Scroll to element defined by options.element
 *
 * @param {Object} options
 * @param {(string | Object)} options.element Selector or DOM element
 * @param {('top' | 'middle' | 'bottom')} [options.alignment='top'] Alignment of the element in the resulting
 *        viewport. Can be one of 'top', 'middle' or 'bottom'. Defaulting to 'top'
 * @param {number} options.additionalOffset Add an additional offset to the final position. if > 0 then page is
 *        moved to the bottom otherwise the page is moved to the top.
 * @param {number} options.durationInMs The amount of time (in milliseconds) the transition should take
 * @param {function} options.onCompleteCallback Called when the transition is complete. Will be called after the
 *        final call to onStep. No argument is passed to this function.
 * @param {boolean} [options.cancelOnUserScroll=true] Possibility to cancel scrolling transition by a users'
 *        scrolling when they scroll by themselves. Defaulting to true
 */
export default function scrollToElement(options) {
	const {
		element,
		alignment,
		additionalOffset,
		durationInMs,
		onCompleteCallback,
		cancelOnUserScroll
	} = options;

	let scrollStepsSum = 0;
	const scrollOffset = calculateScrollOffset(element, additionalOffset, alignment);

	/**
	 * Returns true when currect scroll value is in threshold thus hasn't deviated
	 * from expected value more than the threshold.
	 *
	 * @param {number} scroll Currect scroll value
	 * @returns {boolean}
	 */
	function isScrollInTreshold(scroll) {
		return (
			scroll >= scrollStepsSum - SCROLL_THRESHOLD &&
			scroll <= scrollStepsSum + SCROLL_THRESHOLD
		);
	}

	/**
	 * This fn is called by ease-ease with a new a scroll value. A check if the actuall
	 * scroll position hasn't devieated from expected scroll position is made before
	 * a scroll to the new value is executed.
	 *
	 * @param {number} value
	 */
	function onAnimationStep(value) {
		const roundedValue = Math.round(value);

		if (!cancelOnUserScroll) {
			window.scrollTo(0, roundedValue);
			return;
		}

		const scrollY = Math.round(getScrollY());

		if (isScrollInTreshold(scrollY)) {
			window.scrollTo(0, roundedValue);
			scrollStepsSum = roundedValue;
		}
	}

	if (durationInMs === 0) {
		window.scrollTo(0, scrollOffset);
		onCompleteCallback();
	} else {
		const startValue = (scrollStepsSum = getScrollY());

		ease({
			startValue,
			endValue: scrollOffset,
			durationMs: durationInMs,
			onStep: value => onAnimationStep(value),
			onComplete: () => onCompleteCallback()
		});
	}
}
